/**
 * The weather models we can ask for, and what each one actually is.
 *
 * Open-Meteo fronts a lot of national forecast models for free, and the useful
 * thing about that is not the count, it is that they disagree. Two models saying
 * F5 tomorrow is worth more than one, and six models spanning F3 to F7 is worth
 * knowing BEFORE you leave. So the app carries several and shows them side by
 * side rather than picking one and presenting it as the answer.
 *
 * Every entry carries its real resolution and coverage, because those decide
 * whether a model is worth reading where the boat is. A 25 km global grid puts
 * the Sound of Mull in the same cell as the hills either side of it and will
 * never see the funnelling; a 2 km UK model might. Neither is "better" in the
 * abstract, and a skipper is entitled to know which they are looking at.
 *
 * `days` is what the provider publishes, not a promise: models get cut short and
 * runs get delayed. Treat it as the most you will ever get.
 *
 * The "seamless" variants are Open-Meteo stitching a provider's high-resolution
 * regional model into its global one, so you get the fine grid where it exists
 * and the coarse one beyond. That is usually what you want on a passage that
 * leaves the fine grid partway through, which is why they are the defaults.
 */

/** Where a model has useful data. Coverage is a rough guide, not a boundary. */
export type ModelCoverage = 'global' | 'europe' | 'uk' | 'nordic' | 'north-america';

/**
 * The model ids, as one closed set.
 *
 * There used to be two vocabularies: short names ('gfs', 'ecmwf', 'icon') in the
 * weather-source picker, and the real Open-Meteo ids in this catalogue, with a
 * lookup table between them. That gap is what broke the overlay on 5 Aug: the
 * overlay wrote a catalogue id into the field typed for the short ones, through a
 * cast, and the lookup then returned undefined, so the request went out asking for
 * `models=undefined` and nothing drew. The cast is what let it happen. One set of
 * ids, and the compiler catches it instead of the skipper.
 */
export type RealModelId =
  /** Not a model: several of the others blended. See consensusField.ts. */
  | 'consensus'
  | 'ukmo_seamless'
  | 'ecmwf_ifs'
  | 'icon_seamless'
  | 'gfs_seamless'
  | 'arpege_seamless'
  | 'gem_seamless'
  | 'knmi_seamless';

export interface WeatherModel {
  /** The exact Open-Meteo `models=` value. */
  id: string;
  /** Short name for a picker. */
  name: string;
  /** Who runs it, since that is how sailors talk about forecasts. */
  provider: string;
  /** Grid spacing in km. A range means seamless (fine grid inside a coarse one). */
  resolutionKm: [number, number];
  coverage: ModelCoverage;
  /** Published forecast length, days. */
  days: number;
  /** One line a skipper can act on. */
  note: string;
}

/**
 * The atmospheric models, best-for-UK-waters first, since that is where the boat
 * is. Nothing here needs a key and nothing here is paid.
 */
export const WEATHER_MODELS: (WeatherModel & { id: RealModelId })[] = [
  {
    id: 'ukmo_seamless',
    name: 'UK Met Office',
    provider: 'Met Office',
    resolutionKm: [2, 10],
    coverage: 'uk',
    days: 7,
    note: 'The 2 km UK model where it reaches, the 10 km global beyond it. The one the shipping forecast comes from.',
  },
  {
    id: 'ecmwf_ifs',
    name: 'ECMWF HRES',
    provider: 'ECMWF',
    resolutionKm: [9, 9],
    coverage: 'global',
    days: 15,
    note: 'The best-regarded global model, and the furthest ahead. Coarse enough to miss local funnelling.',
  },
  {
    id: 'icon_seamless',
    name: 'ICON',
    provider: 'DWD (Germany)',
    resolutionKm: [2, 11],
    coverage: 'europe',
    days: 7,
    note: 'German national model. The 7 km European grid covers all UK waters.',
  },
  {
    id: 'gfs_seamless',
    name: 'GFS',
    provider: 'NOAA (US)',
    resolutionKm: [3, 25],
    coverage: 'global',
    days: 16,
    note: 'American global model. Coarse over here, but it runs out furthest of the lot.',
  },
  {
    id: 'arpege_seamless',
    name: 'ARPEGE',
    provider: 'Météo-France',
    resolutionKm: [1, 25],
    coverage: 'europe',
    days: 4,
    note: 'French model, 10 km over Europe. Short range, and it earns its place in the Channel.',
  },
  {
    id: 'gem_seamless',
    name: 'GEM',
    provider: 'Environment Canada',
    resolutionKm: [2.5, 15],
    coverage: 'global',
    days: 10,
    note: 'Canadian model. A genuinely independent opinion, which is the point of having it.',
  },
  {
    id: 'knmi_seamless',
    name: 'HARMONIE',
    provider: 'KNMI (Netherlands)',
    resolutionKm: [2, 2],
    coverage: 'europe',
    days: 2.5,
    note: 'A 2 km European model. Short, but the fine grid shows coastal effects the global models flatten.',
  },
];

/**
 * The wave models. A different endpoint and a different set of providers, so a
 * skipper picks their sea forecast separately from their wind one. That is not
 * an inconvenience: the wind model and the wave model genuinely are different
 * models, and pretending otherwise would hide it.
 */
export const WAVE_MODELS: WeatherModel[] = [
  {
    id: 'ecmwf_wam',
    name: 'ECMWF WAM',
    provider: 'ECMWF',
    resolutionKm: [9, 9],
    coverage: 'global',
    days: 10,
    note: 'The 9 km global wave model. A sensible default anywhere.',
  },
  {
    id: 'dwd_ewam',
    name: 'EWAM',
    provider: 'DWD (Germany)',
    resolutionKm: [5, 5],
    coverage: 'europe',
    days: 4,
    note: 'A 5 km European wave model. The finest grid on offer in home waters.',
  },
  {
    id: 'meteo_france_wave',
    name: 'MFWAM',
    provider: 'Météo-France',
    resolutionKm: [8, 8],
    coverage: 'global',
    days: 5,
    note: 'French global wave model, 8 km.',
  },
  {
    id: 'gfs_wave_025',
    name: 'GFS Wave',
    provider: 'NOAA (US)',
    resolutionKm: [25, 25],
    coverage: 'global',
    days: 10,
    note: 'Coarse at 25 km, so it reads the open ocean better than it reads a sound.',
  },
];

/** Look a model up by its Open-Meteo id, across both catalogues. */
export function modelById(id: string): WeatherModel | null {
  if (id === 'consensus') return CONSENSUS_MODEL;
  return WEATHER_MODELS.find((m) => m.id === id) ?? WAVE_MODELS.find((m) => m.id === id) ?? null;
}

/** "2 to 10 km" / "9 km", for a picker line. */
export function resolutionLabel(m: WeatherModel): string {
  const [lo, hi] = m.resolutionKm;
  const n = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
  return lo === hi ? `${n(lo)} km` : `${n(lo)} to ${n(hi)} km`;
}

/**
 * The blend, which is NOT a model and is deliberately not in the catalogue.
 *
 * It cannot be fetched: there is no `models=consensus` to ask for. It is several
 * of the real models combined per grid point (see consensusField.ts). Putting it
 * in WEATHER_MODELS made it a column in the model-comparison table, which would
 * then have tried to fetch it and shown an empty column with an error in it, and
 * a comparison table with the average as one of the things being compared is a
 * category error anyway.
 *
 * So it lives here, and the two places that legitimately offer it (the overlay's
 * source picker and the weather-source list) add it explicitly.
 */
export const CONSENSUS_MODEL: WeatherModel & { id: 'consensus' } = {
  id: 'consensus',
  name: 'Agreed (all models)',
  provider: 'Met Office, ECMWF, DWD, NOAA',
  resolutionKm: [2, 25],
  coverage: 'global',
  days: 7,
  note: 'Four models averaged with the extreme outliers left out. Not better than the best one, but it does not depend on having picked it.',
};

/** Everything selectable as a wind source for the OVERLAY: the blend, then the models. */
export const OVERLAY_MODELS: (WeatherModel & { id: RealModelId })[] = [
  CONSENSUS_MODEL,
  ...WEATHER_MODELS,
];
